import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["checkbox", "amountInput", "vietqrImage", "vietqrOverlay", "paynowImage", "paynowOverlay"]
  static values = { validateUrl: String }

  connect() {
    this.updateFormState()
    this.updateVietQRImage()
    this.updatePayNowImage()
  }


  checkboxChanged() {
    this.updateFormState()
  }

  amountChanged() {
    // Only update QR codes when amount changes, don't auto-edit the amount field
    this.updateVietQRImage()
    this.updatePayNowImage()
  }

  async updateVietQRImage(newUrl = null) {
    if (newUrl) {
      // Direct URL update with loading overlay
      this.showVietQRLoading()

      // Create a new image to preload
      const img = new Image()

      img.onload = () => {
        // Image loaded successfully, update the src and hide overlay
        this.vietqrImageTarget.src = newUrl
        this.hideVietQRLoading()
      }

      img.onerror = () => {
        // Image failed to load, hide overlay and keep old image
        console.error("Failed to load VietQR image:", newUrl)
        this.hideVietQRLoading()
      }

      // Start loading the image
      img.src = newUrl
    } else {
      // Fetch new URL from API
      const currentAmount = this.amountInputTarget.value.replace(/,/g, "")
      await this.updateVietQRFromValidate(currentAmount)
    }
  }

  showVietQRLoading() {
    if (this.hasVietqrOverlayTarget) {
      this.vietqrOverlayTarget.classList.remove("d-none")
    }
  }

  hideVietQRLoading() {
    if (this.hasVietqrOverlayTarget) {
      this.vietqrOverlayTarget.classList.add("d-none")
    }
  }

  async updateVietQRFromValidate(amount) {
    if (!this.hasVietqrImageTarget || !this.hasValidateUrlValue) {
      return
    }

    try {
      // Get repayment ID from hidden field
      const repaymentIdField = this.element.querySelector('input[name="repayment_id"]')
      const repaymentId = repaymentIdField ? repaymentIdField.value : ''

      // Call the validate endpoint with current amount and repayment ID
      const urlParams = new URLSearchParams()
      urlParams.append('current_amount', amount)
      if (repaymentId) {
        urlParams.append('repayment_id', repaymentId)
      }

      const response = await fetch(`${this.validateUrlValue}?${urlParams.toString()}`, {
        method: 'GET',
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()

      if (data.error) {
        console.error("Error from validate API:", data.message)
        return
      }

      if (data.vietqr_url) {
        this.updateVietQRImage(data.vietqr_url)
      }

    } catch (error) {
      console.error("Error updating VietQR URL:", error)
    }
  }

  async updatePayNowImage(newUrl = null) {
    if (newUrl) {
      // Direct URL update with loading overlay
      this.showPayNowLoading()

      // Create a new image to preload
      const img = new Image()

      img.onload = () => {
        // Image loaded successfully, update the src and hide overlay
        this.paynowImageTarget.src = newUrl
        this.hidePayNowLoading()
      }

      img.onerror = () => {
        // Image failed to load, hide overlay and keep old image
        console.error("Failed to load PayNow image:", newUrl)
        this.hidePayNowLoading()
      }

      // Start loading the image
      img.src = newUrl
    } else {
      // Fetch new URL from API
      const currentAmount = this.amountInputTarget.value.replace(/,/g, "")
      await this.updatePayNowFromValidate(currentAmount)
    }
  }

  showPayNowLoading() {
    if (this.hasPaynowOverlayTarget) {
      this.paynowOverlayTarget.classList.remove("d-none")
    }
  }

  hidePayNowLoading() {
    if (this.hasPaynowOverlayTarget) {
      this.paynowOverlayTarget.classList.add("d-none")
    }
  }

  async updatePayNowFromValidate(amount) {
    if (!this.hasPaynowImageTarget || !this.hasValidateUrlValue) {
      return
    }

    try {
      // Get repayment ID from hidden field
      const repaymentIdField = this.element.querySelector('input[name="repayment_id"]')
      const repaymentId = repaymentIdField ? repaymentIdField.value : ''

      // Call the validate endpoint with current amount and repayment ID
      const urlParams = new URLSearchParams()
      urlParams.append('current_amount', amount)
      if (repaymentId) {
        urlParams.append('repayment_id', repaymentId)
      }

      const response = await fetch(`${this.validateUrlValue}?${urlParams.toString()}`, {
        method: 'GET',
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()

      if (data.error) {
        console.error("Error from validate API:", data.message)
        return
      }

      if (data.paynow_url) {
        this.updatePayNowImage(data.paynow_url)
      }

    } catch (error) {
      console.error("Error updating PayNow URL:", error)
    }
  }

  async updateFormState() {
    const checkedBoxes = this.getCheckedBoxes()
    const currentAmount = this.amountInputTarget.value.replace(/,/g, "")

    try {
      const response = await this.validateEarlyRepayment(checkedBoxes, currentAmount)

      // Only update amount field when checkboxes are selected
      // This prevents overwriting user-entered amounts when no items are selected
      if (checkedBoxes.length > 0) {
        const currentAmountFloat = parseFloat(currentAmount) || 0
        if (response.amount !== undefined && response.amount !== currentAmountFloat) {
          this.amountInputTarget.value = this.formatAmount(response.amount)

          // Trigger the format-number controller if it exists
          const formatEvent = new Event('input', { bubbles: true })
          this.amountInputTarget.dispatchEvent(formatEvent)
        }
      }

      // Update VietQR from the response (this handles both checkbox and amount changes)
      if (response.vietqr_url && this.hasVietqrImageTarget) {
        this.updateVietQRImage(response.vietqr_url)
      }

      // Update PayNow from the response (this handles both checkbox and amount changes)
      if (response.paynow_url && this.hasPaynowImageTarget) {
        this.updatePayNowImage(response.paynow_url)
      }

    } catch (error) {
      console.error("Error validating early repayment:", error)
    }
  }

  async validateEarlyRepayment(checkedBoxes, currentAmount) {
    // Get repayment ID from hidden field
    const repaymentIdField = this.element.querySelector('input[name="repayment_id"]')
    const repaymentId = repaymentIdField ? repaymentIdField.value : ''

    const urlParams = new URLSearchParams()
    urlParams.append('current_amount', currentAmount)
    if (repaymentId) {
      urlParams.append('repayment_id', repaymentId)
    }

    checkedBoxes.forEach(checkbox => {
      if (checkbox.name.startsWith('early_repay_mca_')) {
        urlParams.append('selected_mcas', checkbox.name.replace('early_repay_mca_', ''))
      } else if (checkbox.name.startsWith('due_payment_')) {
        urlParams.append('selected_due_payments', checkbox.name.replace('due_payment_', ''))
      }
    })

    const response = await fetch(`${this.validateUrlValue}?${urlParams.toString()}`, {
      method: 'GET',
      headers: {
        'X-Requested-With': 'XMLHttpRequest'
      }
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    return await response.json()
  }

  getCheckedBoxes() {
    return this.checkboxTargets.filter(checkbox => checkbox.checked)
  }

  formatAmount(amount) {
    // Format amount with commas (matches the format-number controller behavior)
    return parseFloat(amount).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })
  }


  disconnect() {
    clearTimeout(this.debounceTimer)
  }
}
